//gatepass
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Layout from '../../Component/Layout';
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { PURCHASE_API, MASTER_API } from "../../apiConfig";
import { authHeaders } from "../../utils/authHeaders";
import { FileText } from 'lucide-react';

// ── compact react-select styles (same as PurchaseOrder.jsx) ──
const rsCompact = {
  control: (base) => ({
    ...base,
    minHeight: "32px",
    height: "32px",
    fontSize: "13px",
    borderColor: "#ced4da",
    boxShadow: "none",
    "&:hover": { borderColor: "#86b7fe" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({ ...base, height: "32px" }),
  menu: (base) => ({ ...base, zIndex: 99999, fontSize: "13px", position: "absolute" }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
};


// Slightly larger + wider variant, used for the Package Type cell so the
// "Select or type" placeholder fits on one line and there's room to type.
const rsPackageType = {
  ...rsCompact,
  container: (base) => ({ ...base, minWidth: "130px" }),
  control: (base) => ({
    ...rsCompact.control(base),
    minHeight: "38px",
    height: "38px",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 10px" }),
};

const USER_MANUAL_SCOPE = {
    moduleName: "Logistics",
    process: "Inbound",
    subProcess: "Gate Pass"
};
const USER_MANUAL_LIST_API = `${MASTER_API}/user-manual/list`;

function getCurrentUser() {
  const userObj = localStorage.getItem("user");
  if (userObj) {
    try {
      const parsedUser = JSON.parse(userObj);
      // Prefer the actual user id (e.g. "maithili") so the stamped user is the
      // user id, not the name or email.
      if (parsedUser.userId) return parsedUser.userId;
      const fullName = `${parsedUser.firstName || ''} ${parsedUser.lastName || ''}`.trim();
      if (fullName) return fullName;
      if (parsedUser.email) return parsedUser.email;
    } catch (e) { console.error("Error parsing user data:", e); }
  }
  const storedUserId = localStorage.getItem("userId");
  if (storedUserId && storedUserId !== "undefined" && storedUserId !== "null") return storedUserId;
  const storedName = localStorage.getItem("userName");
  if (storedName && storedName !== "undefined" && storedName !== "null") return storedName;
  return "";
}

// Reads the JWT's own "role" claim rather than trusting a separate
// localStorage "role" key — that key turned out to not exist in this app
// at all (confirmed: localStorage.getItem("role") -> null even for a real
// hardcoded admin login), which was silently defeating every admin-bypass
// check below. The token itself always has the real role, wherever it's
// stored, so decode it directly instead of guessing another storage key.
function getAuthRole() {
  let token = localStorage.getItem("token");
  if (!token) {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      token = user?.token || null;
    } catch (e) {
      token = null;
    }
  }
  if (!token) return null;
  try {
    // JWTs are base64url-encoded; atob() needs standard base64 + padding.
    const b64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4)));
    // Lower-cased so "Admin" / "ADMIN" still match the admin check.
    return payload?.role ? String(payload.role).toLowerCase() : null;
  } catch (e) {
    return null;
  }
}

// Reads the INBOUND_GATE_PASS permissions the login response stored, e.g.
// [{ module_id: "INBOUND_GATE_PASS", can_view: 1, can_create: 1, can_edit: 0 }, ...]
// Same shape/localStorage keys as Goodsreceipt.js's getGRNPermission —
// admins (role decoded from the JWT itself) always pass.
function getGatePassPermission() {
  const role = getAuthRole();
  if (role === "admin") {
    return { can_view: true, can_create: true, can_edit: true };
  }
  try {
    // Login stores permissions inside the "user" object (user.permissions);
    // a standalone "permissions" key is kept only as a fallback.
    let permissions = [];
    try {
      const userObj = JSON.parse(localStorage.getItem("user") || "{}");
      if (Array.isArray(userObj?.permissions)) permissions = userObj.permissions;
    } catch (e) { /* ignore, fall through to fallback */ }
    if (permissions.length === 0) {
      permissions = JSON.parse(localStorage.getItem("permissions") || "[]");
    }
    const gp = permissions.find((p) => p.module_id === "INBOUND_GATE_PASS");
    return {
      can_view: !!gp?.can_view,
      can_create: !!gp?.can_create,
      can_edit: !!gp?.can_edit
    };
  } catch (e) {
    console.error("Error reading Gate Pass permissions:", e);
    return { can_view: false, can_create: false, can_edit: false };
  }
}


const getTodayDateStr = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const formatDateDMY = (dateStr) => {
  if (!dateStr) return "";
  const raw = String(dateStr);
  const isoDate = raw.includes("T")
    ? raw.split("T")[0]
    : raw.includes(" ")
    ? raw.split(" ")[0]
    : raw;
  const parts = isoDate.split("-");
  if (parts.length !== 3) return isoDate;
  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
};


const emptyHeader = () => ({
  gate_pass_no: "",
  // Kept internally (not shown on the form) so existing list/query logic
  // and the DB schema keep working — always fixed for an inward/PO gate pass.
  gate_pass_type: "Vehicle Movement",
  location: "",
  department: "",
  requestor_name: getCurrentUser() || "",
  request_datetime: getTodayDateStr(),
  reference_doc_type: "Purchase Order",
  reference_doc_no: "",
  delivery_note_no: "",
  vendor_name_address: "",
  in_time: "",
  security_name_in: "",
  out_time: "",
  security_name_out: "",
  security_comments: "",
  received_by: "",
  received_date: getTodayDateStr(),
  vehicle_number: "",
  driver_name: "",
  status: "DRAFT",
});

// Search filters shown above the Gate Pass list table (NOT required to
// enable Create — Create is always available).
const emptySearchFilters = () => ({
  po_number: "",
  vehicle_number: "",
  delivery_note_no: "",
  date: "",
});

const GatePass = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [gatePassList, setGatePassList] = useState([]);
  const gatePassPermission = getGatePassPermission();
  const [showModal, setShowModal] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manualExists, setManualExists] = useState(null);
  const [manualFile, setManualFile] = useState(null);
  const [isEditMode, setIsEditMode] = useState(true);
  const [activeTab, setActiveTab] = useState("product");

  const [header, setHeader] = useState(emptyHeader());
  const [products, setProducts] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [docsUploading, setDocsUploading] = useState(false);
  // Gate Pass attachment upload size limit — configurable via
  // Configuration → File Size Config (Module = "Gate Pass"). Falls back
  // to 50 KB if it can't be fetched.
  const DEFAULT_MAX_GATEPASS_ATTACHMENT_SIZE_KB = 50;
  const [maxGatePassAttachmentSizeKb, setMaxGatePassAttachmentSizeKb] = useState(DEFAULT_MAX_GATEPASS_ATTACHMENT_SIZE_KB);
  const [searchFilters, setSearchFilters] = useState(emptySearchFilters());
  const [showGatePassViewModal, setShowGatePassViewModal] = useState(false);
  // Status toggle above the list table — "not_done" | "done"
  const [statusFilter, setStatusFilter] = useState("not_done");

  //eslint-disable-next-line
  const [locations, setLocations] = useState([]);
  //eslint-disable-next-line
  const [departments, setDepartments] = useState([]);
  const [packageTypes, setPackageTypes] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  //eslint-disable-next-line
  const [productMaster, setProductMaster] = useState([]);

  // ================= INITIAL LOAD =================
  useEffect(() => {
    // Gate Pass list
    axios.get(`${PURCHASE_API}/gatepass/list`, { headers: authHeaders() })
      .then(res => setGatePassList(res.data?.gatePasses || res.data || []))
      .catch(err => console.error("Gate Pass list error:", err));

    // Location (Configuration → Master Data → Location)
    axios.get(`${MASTER_API}/configuration/location`, { headers: authHeaders() })
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        setLocations(list);
      })
      .catch(err => console.error("Location error:", err));

    // Department (Configuration → Master Data → Department, served via the generic /dynamic/:type route)
    axios.get(`${MASTER_API}/configuration/dynamic/department`, { headers: authHeaders() })
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        setDepartments(list);
      })
      .catch(err => console.error("Department error:", err));

    // Package Type (Configuration → Master Data → Package Type, served via the generic /dynamic/:type route)
    axios.get(`${MASTER_API}/configuration/dynamic/package-type`, { headers: authHeaders() })
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        setPackageTypes(list.map(item => item.name).filter(Boolean));
      })
      .catch(err => console.error("Package Type error:", err));

    // Purchase Orders for Reference Document dropdown, AND for the
    // "Gate Pass Not Done" rows shown in the list table below — only
    // POs that already have a GRN posted but no Gate Pass raised yet.
    axios.get(`${PURCHASE_API}/gatepass/po-pending`, { headers: authHeaders() })
      .then(res => setPurchaseOrders(res.data?.purchaseOrders || []))
      .catch(err => console.error("PO list error:", err));

    // Product master (for the "+ Add Product" Product Code dropdown) —
    // same source as the Goods Receipt product picker.
    axios.get(`${PURCHASE_API}/gatepass/products`, { headers: authHeaders() })
      .then(res => setProductMaster(res.data?.products || []))
      .catch(err => console.error("Product master list error:", err));
  }, []);

  // Automatically open the Gate Pass view modal if the URL tells us to (e.g. coming from
  // the "Gate Pass No" hyperlink on the Goods Receipt screen).
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const viewGatePassNo = queryParams.get("view");

    if (viewGatePassNo && !showModal) {
      viewGatePass(viewGatePassNo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const refreshList = () => {
    axios.get(`${PURCHASE_API}/gatepass/list`, { headers: authHeaders() })
      .then(res => setGatePassList(res.data?.gatePasses || res.data || []))
      .catch(err => console.error("Gate Pass list error:", err));

    axios.get(`${PURCHASE_API}/gatepass/po-pending`, { headers: authHeaders() })
      .then(res => setPurchaseOrders(res.data?.purchaseOrders || []))
      .catch(err => console.error("PO list error:", err));
  };

  // ================= CREATE / OPEN =================
  // Create is always available — no pre-filter gating. Opens a blank
  // form; the PO Number field inside loads the products.
  const openCreate = () => {
    setHeader(emptyHeader());
    setProducts([]);
    setAttachments([]);
    setActiveTab("product");
    setIsEditMode(true);
    setShowModal(true);
  };

  // Opens the create modal pre-filled from a "Gate Pass Not Done" row —
  // i.e. a PO that already has a GRN posted but no gate pass yet.
  // Header stays editable (isEditMode=true) so the user fills in the
  // remaining mandatory fields, then Save generates the gate pass number.
  const openFromPendingPO = (poId) => {
    setHeader({
      ...emptyHeader(),
      reference_doc_type: "Purchase Order",
      reference_doc_no: poId,
    });
    setProducts([]);
    setAttachments([]);
    setActiveTab("product");
    setIsEditMode(true);
    setShowModal(true);
    handleReferenceDocSelect(poId, "Purchase Order");
  };

  const viewGatePass = async (gatePassNo) => {
    try {
      const res = await axios.get(`${PURCHASE_API}/gatepass/${gatePassNo}`, { headers: authHeaders() });
      const data = res.data?.gatePass || res.data;
      if (!data) return;

      setHeader({
        gate_pass_no: data.gate_pass_no || "",
        gate_pass_type: data.gate_pass_type || "",
        location: data.location || "",
        department: data.department || "",
        requestor_name: data.requestor_name || "",
        request_datetime: data.request_datetime || getTodayDateStr(),
        reference_doc_type: data.reference_doc_type || "Purchase Order",
        reference_doc_no: data.reference_doc_no || "",
        delivery_note_no: data.delivery_note_no || "",
        vendor_name_address: data.vendor_name_address || "",
        in_time: data.in_time || "",
        security_name_in: data.security_name_in || "",
        out_time: data.out_time || "",
        security_name_out: data.security_name_out || "",
        security_comments: data.security_comments || "",
        received_by: data.received_by || "",
        received_date: data.received_date || "",
        vehicle_number: data.vehicle_number || "",
        driver_name: data.driver_name || "",
        status: data.status || "DRAFT",
      });
      setProducts(data.products || []);
      setAttachments(data.attachments || []);
      setActiveTab("product");
      setIsEditMode(false);
      setShowModal(true);
    } catch (err) {
      console.error("Error viewing Gate Pass:", err);
      toast.error("Failed to load Gate Pass details");
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setHeader(emptyHeader());
    setProducts([]);
    setAttachments([]);
    setActiveTab("product");
    setIsEditMode(true);

    // Handle navigation back to the previous screen (when this Gate Pass was
    // opened via a hyperlink from another screen, e.g. Goods Receipt's
    // "Gate Pass No" column)
    const queryParams = new URLSearchParams(location.search);
    const returnTo = queryParams.get("returnTo");

    if (returnTo) {
      navigate(returnTo);
    } else if (queryParams.get("view")) {
      navigate(location.pathname, { replace: true });
    }
  };

  // ================= REFERENCE DOCUMENT AUTO-POPULATE =================
  const handleReferenceDocSelect = async (docNo, docTypeOverride) => {
    const docType = docTypeOverride || header.reference_doc_type || "Purchase Order";
    const selectedPo = purchaseOrders.find(p => p.purchase_order_id === docNo);

    setHeader(prev => ({
      ...prev,
      reference_doc_no: docNo,
      reference_doc_type: docType,
      vendor_name_address: selectedPo?.vendor_name || prev.vendor_name_address,
    }));

    if (!docNo) {
      setProducts([]);
      return;
    }

    try {
      if (docType === "Purchase Order") {
        // Products now come from the Gate Pass module's own endpoint, so:
        //  - no dependency on Goods Receipt permissions
        //  - works for POs that don't have a GRN yet
        //  - Import POs automatically use the saved Custom PO actuals
        const res = await axios.get(
          `${PURCHASE_API}/gatepass/po-products/${encodeURIComponent(docNo)}`,
          { headers: authHeaders() }
        );

        const poData = Array.isArray(res.data?.products) ? res.data.products : [];

        if (!poData.length) {
          setProducts([]);
          toast.warning("No product lines found for this Purchase Order. You can add products manually.");
          return;
        }

        setProducts(
          poData.map(row => ({
            product_code: row.product_code || "",
            product_description: row.description || "",
            package_type: "",
            number: "",
            quantity: row.quantity ?? "",
            received_quantity: "",
            quantity_uom: row.uom || "",
            weight: "",
            weight_uom: row.uom || "",
            reason_for_movement: "",
            grn_number: "",
            batch_number: "",
          }))
        );
      } else if (docType === "Sales Order") {
        const res = await axios.get(`${MASTER_API}/sales/sales-orders/${docNo}`, { headers: authHeaders() });
        const soData = Array.isArray(res.data) ? res.data : (res.data?.items || []);
        if (soData.length) {
          setProducts(
            soData.map(row => ({
              product_code: row.product_code || "",
              product_description: row.description || row.product_description || "",
              package_type: "",
              number: "",
              quantity: row.quantity || row.order_qty || "",
              received_quantity: "",
              quantity_uom: row.uom || "",
              weight: "",
              weight_uom: row.uom || "",
              reason_for_movement: "",
              grn_number: "",
              batch_number: "",
            }))
          );
        }
      }
    } catch (err) {
      console.error("Reference document fetch failed:", err);
      setProducts([]);

      const status = err.response?.status;
      const serverMsg = err.response?.data?.message;

      if (status === 409) {
        // Import PO whose actuals aren't saved on the Custom screen yet
        toast.error(serverMsg || "This Import PO's actuals haven't been saved yet.");
      } else if (status === 403) {
        toast.error("You do not have permission to load products for this document.");
      } else {
        toast.warning(serverMsg || "Could not auto-populate from reference document. Please enter details manually.");
      }
    }
  };

  // ================= PRODUCT TAB HANDLERS =================
  // Rows load automatically from a selected Reference Document (PO/SO), but a
  // Gate Pass can also be raised without a PO — in that case products are
  // added and typed in manually here.
  const handleProductFieldChange = (idx, field, value) => {
    setProducts(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const addManualProduct = () => {
    setProducts(prev => ([
      ...prev,
      {
        manual: true,
        product_code: "",
        product_description: "",
        package_type: "",
        number: "",
        quantity: "",
        received_quantity: "",
        quantity_uom: "",
        weight: "",
        reason_for_movement: "",
      },
    ]));
  };

  // Selecting a Product Code from the dropdown auto-fills Description + UOM
  // from the product master — same behaviour as the Goods Receipt product picker.
  const handleManualProductSelect = (idx, productCode) => {
    const match = productMaster.find(p => p.productCode === productCode);
    setProducts(prev => {
      const updated = [...prev];
      updated[idx] = {
        ...updated[idx],
        product_code: productCode || "",
        product_description: match?.productDescription || "",
        quantity_uom: match?.uom || "",
      };
      return updated;
    });
  };

  const removeProduct = (idx) => {
    setProducts(prev => prev.filter((_, i) => i !== idx));
  };

  // ================= ATTACHMENTS =================
  // Fetch the configured upload size limit for Gate Pass attachments.
  useEffect(() => {
    fetch(`${MASTER_API}/configuration/file-size-limit/${encodeURIComponent("Gate Pass")}`)
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(data => {
        if (data && data.maxSizeKb) setMaxGatePassAttachmentSizeKb(data.maxSizeKb);
      })
      .catch(() => { /* keep default limit if config fetch fails */ });
  }, []);

  // Splits a FileList/array into files within the limit and files that
  // exceed it, and toasts a warning listing any rejected files.
  const filterAttachmentsWithinSizeLimit = (files) => {
    const maxBytes = maxGatePassAttachmentSizeKb * 1024;
    const okFiles = [];
    const rejected = [];
    files.forEach(f => {
      if (f.size > maxBytes) {
        rejected.push(f.name);
      } else {
        okFiles.push(f);
      }
    });
    if (rejected.length) {
      toast.error(
        `${rejected.length} file(s) exceed the ${maxGatePassAttachmentSizeKb} KB upload limit and were skipped: ${rejected.join(', ')}`
      );
    }
    return okFiles;
  };

  // If the Gate Pass is already saved (has a gate_pass_no), files upload
  // immediately via their own endpoint — no need to be in Edit mode.
  // For a brand-new, not-yet-saved Gate Pass, files are queued locally
  // and sent along with the first Save (create).
  const handleAttachmentUpload = async (e) => {
    const files = filterAttachmentsWithinSizeLimit(Array.from(e.target.files));
    if (!files.length) { e.target.value = ""; return; }

    if (header.gate_pass_no) {
      const formData = new FormData();
      files.forEach((f) => formData.append("attachments", f));
      setDocsUploading(true);
      try {
        const res = await axios.post(
          `${PURCHASE_API}/gatepass/${header.gate_pass_no}/attachments`,
          formData,
          { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } }
        );
        setAttachments(res.data?.attachments || []);
        toast.success("Attachment added");
      } catch (err) {
        console.error("Attachment upload error:", err);
        toast.error("Failed to add attachment");
      } finally {
        setDocsUploading(false);
      }
    } else {
      setAttachments(prev => [...prev, ...files]);
    }
    e.target.value = "";
  };

  // Downloads an attachment already on Drive — mirrors Goodsreceipt.js's
  // handleDocDownload.
  const handleAttachmentDownload = async (file) => {
    if (!header.gate_pass_no || !file.id) return;
    try {
      const res = await fetch(
        `${PURCHASE_API}/gatepass/${header.gate_pass_no}/attachments/${file.id}/download`,
        { headers: authHeaders() }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `Download failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.file_name || `Attachment`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || "Failed to download file");
    }
  };

  const removeAttachment = async (idx) => {
    const file = attachments[idx];
    if (file && file.id && header.gate_pass_no) {
      try {
        const res = await axios.delete(
          `${PURCHASE_API}/gatepass/${header.gate_pass_no}/attachments/${file.id}`,
          { headers: authHeaders() }
        );
        setAttachments(res.data?.attachments || []);
        toast.success("Attachment removed");
      } catch (err) {
        console.error("Attachment delete error:", err);
        toast.error("Failed to remove attachment");
      }
    } else {
      setAttachments(prev => prev.filter((_, i) => i !== idx));
    }
  };

  // ================= SAVE =================
  const handleSave = async () => {
    // Mandatory header validation — P.O Number is optional; a Gate Pass can
    // be raised with manually added products instead.
    if (!header.request_datetime) return toast.warning("Date is mandatory");
    if (!header.vehicle_number) return toast.warning("Vehicle No is mandatory");

    if (products.length === 0) {
      return toast.warning("Add at least one product (via P.O Number or + Add Product) before saving");
    }
    if (products.some(p => !String(p.product_code || "").trim() && !String(p.product_description || "").trim())) {
      return toast.warning("Each product row needs a Product Code or Description");
    }

    const formData = new FormData();
    formData.append("header", JSON.stringify(header));
    formData.append("products", JSON.stringify(products));
    attachments.forEach((file, i) => {
      if (file instanceof File) formData.append("attachments", file);
    });

    const isNew = !header.gate_pass_no;
    const apiUrl = isNew
      ? `${PURCHASE_API}/gatepass/create`
      : `${PURCHASE_API}/gatepass/update`;

    try {
      const res = await axios.post(apiUrl, formData, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      if (res.data.success) {
        toast.success(
          isNew
            ? `Gate Pass Created: ${res.data.gate_pass_no}`
            : "Gate Pass updated successfully"
        );
        setHeader(prev => ({ ...prev, gate_pass_no: res.data.gate_pass_no || prev.gate_pass_no }));
        setIsEditMode(false);
        refreshList();
      } else {
        toast.error(res.data.message || "Failed to save Gate Pass");
      }
    } catch (err) {
      console.error("Save Gate Pass error:", err);
      toast.error("Error while saving Gate Pass");
    }
  };

  // Mandatory fields that must be filled before the record can be saved.
  // P.O Number is optional — at least one product row (via PO or manually
  // added) is required, checked separately in handleSave.
  const isHeaderValid =
    !!header.request_datetime &&
    !!header.vehicle_number;

  // ================= LIST SEARCH (filters the table only, doesn't gate Create) =================
  const matchesSearch = (row) => {
    const poNo = (row.purchase_order_id || row.reference_doc_no || "").toLowerCase();
    const vehicleNo = (row.vehicle_number || "").toLowerCase();
    const deliveryNoteNo = (row.delivery_note_no || "").toLowerCase();
    const rowDate = row.received_date || row.request_datetime || "";

    if (searchFilters.po_number && !poNo.includes(searchFilters.po_number.toLowerCase())) return false;
    if (searchFilters.vehicle_number && !vehicleNo.includes(searchFilters.vehicle_number.toLowerCase())) return false;
    if (searchFilters.delivery_note_no && !deliveryNoteNo.includes(searchFilters.delivery_note_no.toLowerCase())) return false;
    if (searchFilters.date && !String(rowDate).startsWith(searchFilters.date)) return false;
    return true;
  };

  const filteredPendingPOs = purchaseOrders.filter(matchesSearch);
  const filteredGatePassList = gatePassList.filter(matchesSearch);

  // Apply the status toggle on top of the search filters above.
  const visiblePendingPOs = statusFilter === "done" ? [] : filteredPendingPOs;
  const visibleGatePassList = statusFilter === "not_done" ? [] : filteredGatePassList;

  return (
    <Layout>
      <style>{`
        @media (max-width: 768px) {
          .gp-page-title { font-size: 20px !important; }
          .gp-modal { padding: 10px !important; }
          .gp-tabs { flex-wrap: wrap; }
          .gp-tabs button { font-size: 12px !important; padding: 3px 7px !important; }
        }
        .gp-table-wrapper { overflow-x: auto; -webkit-overflow-scrolling: touch; }
      `}</style>

      <div style={styles.pageTitleBar}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", flexWrap: "wrap", gap: "12px" }}>
          <div>
            <h2 style={styles.pageTitle} className="gp-page-title">Gate Pass</h2>
            <div style={styles.pageSubtitle}>Manage inbound/outbound gate passes</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <button className="btn btn-dark" onClick={openCreate} disabled={!gatePassPermission.can_create} style={!gatePassPermission.can_create ? { opacity: 0.5, cursor: "not-allowed" } : undefined}>
            + Create
          </button>
          <button
    onClick={() => {
    setManualExists(null);
    setManualFile(null);
    setShowManual(true);
    fetch(USER_MANUAL_LIST_API, { headers: authHeaders() })
        .then((res) => res.json())
        .then((data) => {
            const norm = (v) => String(v || "").trim().toLowerCase();
            const match = (data?.data || []).find((m) =>
                norm(m.module_name) === norm(USER_MANUAL_SCOPE.moduleName) &&
                norm(m.process_name) === norm(USER_MANUAL_SCOPE.process) &&
                norm(m.sub_process_name) === norm(USER_MANUAL_SCOPE.subProcess)
            );
            if (match && match.drive_link) {
                setManualFile(match);
                setManualExists(true);
            } else {
                setManualExists(false);
            }
        })
        .catch(() => setManualExists(false));
}}
    title="View User Manual"
    style={{
      background: "#ffffff",
      border: "1px solid #e2e8f0",
      borderRadius: "10px",
      padding: "10px 14px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      cursor: "pointer",
      color: "#3b82f6",
      fontSize: "14px",
      fontWeight: "600",
      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    }}
    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#3b82f6"; }}
    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; }}
  >
    <FileText size={18} />
    User Manual
  </button>
        </div>
      </div>
      </div>

      {showManual && (
  <div
    onClick={() => setShowManual(false)}
    style={{
      position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
      backgroundColor: "rgba(15, 23, 42, 0.6)", display: "flex",
      alignItems: "center", justifyContent: "center", zIndex: 1100,
    }}
  >
    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: "#ffffff",
                            borderRadius: "12px",
                            width: "90vw",
                            height: "97vh",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                            boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)",
                        }}
                    >
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "14px 20px", borderBottom: "1px solid #e2e8f0",
      }}>
        <h4 style={{ margin: 0, color: "#0f172a", fontSize: "16px", fontWeight: "600" }}>
          User Manual
        </h4>
        <button
          onClick={() => setShowManual(false)}
          style={{
            border: "none", background: "#f1f5f9", borderRadius: "8px",
            width: "32px", height: "32px", cursor: "pointer", fontSize: "18px",
            color: "#475569", lineHeight: 1,
          }}
          title="Close"
        >
          ×
        </button>
      </div>

      {manualExists === null && (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: "14px" }}>
          Checking for user manual...
        </div>
      )}

      {manualExists === false && (
        <div style={{
          flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", color: "#64748b", gap: "10px", padding: "20px", textAlign: "center"
        }}>
          <FileText size={40} color="#cbd5e1" />
          <div style={{ fontSize: "15px", fontWeight: 600, color: "#334155" }}>
            User Manual Not Available
          </div>
          <div style={{ fontSize: "13px", color: "#94a3b8", maxWidth: "320px" }}>
            The user manual for this module hasn't been uploaded yet. Please check back later or contact your administrator.
          </div>
        </div>
      )}

      {manualExists === true && manualFile && (() => {
    const MANUAL_SCALE = 1.25;
    return (
        <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
            <iframe
                src={manualFile.drive_link.replace("/view", "/preview")}
                title={manualFile.file_name || "User Manual"}
                allow="autoplay"
                style={{
                    border: "none",
                    width: `${100 / MANUAL_SCALE}%`,
                    height: `${100 / MANUAL_SCALE}%`,
                    transform: `scale(${MANUAL_SCALE})`,
                    transformOrigin: "top left",
                }}
            />
        </div>
    );
})()}
    </div>
  </div>
)}

      {/* ================= SEARCH (filters the table below only — Create is always available) ================= */}
      <div style={styles.filterCard}>
        <div className="row g-2">
          <div className="col-md-3">
            <label style={styles.filterLabel}>P.O Number</label>
            <input
              className="form-control"
              style={{ fontSize: "13px", height: "32px", padding: "4px 8px" }}
              placeholder="Search P.O Number"
              value={searchFilters.po_number}
              onChange={(e) => setSearchFilters(prev => ({ ...prev, po_number: e.target.value }))}
            />
          </div>
          <div className="col-md-3">
            <label style={styles.filterLabel}>Vehicle No</label>
            <input
              className="form-control"
              style={{ fontSize: "13px", height: "32px", padding: "4px 8px" }}
              placeholder="Search Vehicle No"
              value={searchFilters.vehicle_number}
              onChange={(e) => setSearchFilters(prev => ({ ...prev, vehicle_number: e.target.value }))}
            />
          </div>
          <div className="col-md-3">
            <label style={styles.filterLabel}>Delivery Note No</label>
            <input
              className="form-control"
              style={{ fontSize: "13px", height: "32px", padding: "4px 8px" }}
              placeholder="Search Delivery Note No"
              value={searchFilters.delivery_note_no}
              onChange={(e) => setSearchFilters(prev => ({ ...prev, delivery_note_no: e.target.value }))}
            />
          </div>
          <div className="col-md-3">
            <label style={styles.filterLabel}>Date</label>
            <input
              type="date"
              className="form-control"
              style={{ fontSize: "13px", height: "32px", padding: "4px 8px" }}
              value={searchFilters.date}
              onChange={(e) => setSearchFilters(prev => ({ ...prev, date: e.target.value }))}
            />
          </div>
        </div>
      </div>

      {/* ================= STATUS TOGGLE ================= */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
        <div style={{ display: "flex", border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
          {[
            { key: "not_done", label: "Gate Pass Not Done" },
            { key: "done", label: "Gate Pass Done" },
          ].map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setStatusFilter(opt.key)}
              style={{
                fontSize: 13,
                fontWeight: 600,
                padding: "8px 18px",
                cursor: "pointer",
                border: "none",
                borderLeft: opt.key === "done" ? "1px solid #e2e8f0" : "none",
                background: statusFilter === opt.key ? "#1e293b" : "#fff",
                color: statusFilter === opt.key ? "#fff" : "#334155",
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* ================= LIST TABLE (mirrors the paper Inward Gate Entry register) ================= */}
      <div style={styles.tableContainer} className="gp-table-wrapper">
        <table className="table" style={styles.erpTable}>
          <thead>
            <tr>
              <th style={{ ...styles.erpTh, width: 50, textAlign: "center" }}>SL.NO</th>
              <th style={{ ...styles.erpTh, width: 140 }}>Gate Pass No</th>
              <th style={{ ...styles.erpTh, width: 110 }}>Date</th>
              <th style={{ ...styles.erpTh, width: 120 }}>Vehicle No</th>
              <th style={{ ...styles.erpTh, width: 150 }}>P.O Number</th>
              <th style={{ ...styles.erpTh, width: 140 }}>Delivery Note No</th>
              <th style={{ ...styles.erpTh, width: 220 }}>Vendor Name &amp; Address</th>
              <th style={{ ...styles.erpTh, width: 150 }}>Status</th>
              <th style={{ ...styles.erpTh, width: 90, textAlign: "center" }}></th>
            </tr>
          </thead>
          <tbody>
            {visibleGatePassList.length === 0 && visiblePendingPOs.length === 0 ? (
              <tr>
                <td colSpan="9" style={{ ...styles.erpTd, textAlign: "center", padding: 20 }}>
                  No records found
                </td>
              </tr>
            ) : (
              <>
                {/* Pending: POs with GRN posted but Gate Pass not yet raised */}
                {visiblePendingPOs.map((po, idx) => (
                  <tr key={`pending-${idx}`}>
                    <td style={{ ...styles.erpTd, textAlign: "center", color: "#94a3b8" }}>{idx + 1}</td>
                    <td style={{ ...styles.erpTd, color: "#94a3b8" }}>—</td>
                    <td style={styles.erpTd}>{po.received_date ? formatDateDMY(po.received_date) : "—"}</td>
                    <td style={{ ...styles.erpTd, color: "#94a3b8" }}>—</td>
                    <td style={styles.erpTd}>
                      <button
                        style={{ background: "none", border: "none", color: "#1d4ed8", textDecoration: "underline", cursor: "pointer", padding: 0, fontSize: "13px", fontWeight: 600 }}
                        onClick={() => navigate("/cable/procurement/purchase-order", { state: { openPoNo: po.purchase_order_id } })}
                      >
                        {po.purchase_order_id}
                      </button>
                    </td>
                    <td style={{ ...styles.erpTd, color: "#94a3b8" }}>—</td>
                    <td style={styles.erpTd}>{po.vendor_name || "—"}</td>
                    <td style={{ ...styles.erpTd, color: "#dc2626", fontWeight: 600 }}>Gate Pass Not Done</td>
                    <td style={{ ...styles.erpTd, textAlign: "center" }}>
                      <button
                        className="btn btn-outline-dark btn-sm py-0"
                        style={{ fontSize: "12px" }}
                        onClick={() => openFromPendingPO(po.purchase_order_id)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}

                {/* Existing gate passes already created */}
                {visibleGatePassList.map((row, idx) => (
                  <tr key={idx}>
                    <td style={{ ...styles.erpTd, textAlign: "center" }}>{visiblePendingPOs.length + idx + 1}</td>
                    <td style={{ ...styles.erpTd, fontWeight: 600 }}>
                      <button
                        style={{ background: "none", border: "none", color: "#1d4ed8", textDecoration: "underline", cursor: "pointer", padding: 0, fontSize: "13px", fontWeight: 600 }}
                        onClick={() => viewGatePass(row.gate_pass_no)}
                      >
                        {row.gate_pass_no}
                      </button>
                    </td>
                    <td style={styles.erpTd}>{formatDateDMY(row.request_datetime)}</td>
                    <td style={styles.erpTd}>{row.vehicle_number || "—"}</td>
                    <td style={styles.erpTd}>
                      {row.reference_doc_no ? (
                        <button
                          style={{ background: "none", border: "none", color: "#1d4ed8", textDecoration: "underline", cursor: "pointer", padding: 0, fontSize: "13px", fontWeight: 600 }}
                          onClick={() => navigate("/cable/procurement/purchase-order", { state: { openPoNo: row.reference_doc_no } })}
                        >
                          {row.reference_doc_no}
                        </button>
                      ) : "—"}
                    </td>
                    <td style={styles.erpTd}>{row.delivery_note_no || "—"}</td>
                    <td style={{ ...styles.erpTd, whiteSpace: "normal", wordBreak: "break-word" }}>{row.vendor_name_address || "—"}</td>
                    <td style={{ ...styles.erpTd, color: "#166534", fontWeight: 600 }}>Gate Pass Done</td>
                    <td style={{ ...styles.erpTd, textAlign: "center" }}>
                      <button
                        className="btn btn-outline-dark btn-sm py-0"
                        style={{ fontSize: "12px" }}
                        onClick={() => viewGatePass(row.gate_pass_no)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* ================= CREATE / VIEW MODAL ================= */}
      {showModal && (
        <div style={styles.modalBackdrop}>
          <div style={{ ...styles.modal, maxWidth: "1100px", width: "90%", maxHeight: "90vh", padding: "16px", fontSize: "14px" }} className="gp-modal">

            {/* HEADER BAR */}
            <div style={{ ...styles.modalHeader, padding: "10px 14px" }}>
              <div style={{ fontWeight: 600, fontSize: "18px" }}>
                Gate Pass {header.gate_pass_no ? `: ${header.gate_pass_no}` : "(New)"}
              </div>
              <button style={styles.iconBtn} onClick={closeModal}>✕</button>
            </div>

            <div style={{ ...styles.modalBody, padding: "14px" }}>

              {/* ================= HEADER FIELDS — matches the paper Inward Gate Entry form ================= */}

              {/* Row 1 — Date, Vehicle No, P.O Number */}
              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <label style={styles.compactLabel}>Date *</label>
                  <input
                    type="date"
                    className="form-control"
                    style={styles.compactInput}
                    value={header.request_datetime}
                    disabled={!isEditMode}
                    onChange={(e) => setHeader(prev => ({ ...prev, request_datetime: e.target.value }))}
                  />
                </div>

                <div className="col-md-4">
                  <label style={styles.compactLabel}>Vehicle No *</label>
                  <input
                    className="form-control"
                    style={styles.compactInput}
                    value={header.vehicle_number}
                    disabled={!isEditMode}
                    onChange={(e) => setHeader(prev => ({ ...prev, vehicle_number: e.target.value }))}
                  />
                </div>

                <div className="col-md-4">
                  <label style={styles.compactLabel}>P.O Number</label>
                  {isEditMode ? (
                    <Select
                      styles={rsCompact}
                      isClearable
                      placeholder="Select PO"
                      options={purchaseOrders.map(po => ({ value: po.purchase_order_id, label: po.purchase_order_id }))}
                      value={header.reference_doc_no ? { value: header.reference_doc_no, label: header.reference_doc_no } : null}
                      onChange={(opt) => handleReferenceDocSelect(opt?.value || "", "Purchase Order")}
                    />
                  ) : (
                    <div style={{ height: "32px", display: "flex", alignItems: "center" }}>
                      {header.reference_doc_no ? (
                        <button
                          style={{ background: "none", border: "none", color: "#1d4ed8", textDecoration: "underline", cursor: "pointer", padding: 0, fontSize: "13px", fontWeight: 600 }}
                          onClick={() => navigate("/cable/procurement/purchase-order", { state: { openPoNo: header.reference_doc_no } })}
                        >
                          {header.reference_doc_no}
                        </button>
                      ) : "—"}
                    </div>
                  )}
                </div>
              </div>

              {/* Row 2 — Delivery Note No, Vendor Name & Address */}
              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <label style={styles.compactLabel}>Delivery Note No</label>
                  <input
                    className="form-control"
                    style={styles.compactInput}
                    value={header.delivery_note_no}
                    disabled={!isEditMode}
                    onChange={(e) => setHeader(prev => ({ ...prev, delivery_note_no: e.target.value }))}
                  />
                </div>

                <div className="col-md-8">
                  <label style={styles.compactLabel}>Vendor Name &amp; Address</label>
                  <input
                    className="form-control"
                    style={styles.compactInput}
                    placeholder="Auto-filled from P.O Number, editable"
                    value={header.vendor_name_address}
                    disabled={!isEditMode}
                    onChange={(e) => setHeader(prev => ({ ...prev, vendor_name_address: e.target.value }))}
                  />
                </div>
              </div>

              {/* Row 3 — In Time / Security Name, Out Time / Security Name */}
              <div className="row g-3 mb-3">
                <div className="col-md-3">
                  <label style={styles.compactLabel}>In Time</label>
                  <input
                    type="time"
                    className="form-control"
                    style={styles.compactInput}
                    value={header.in_time}
                    disabled={!isEditMode}
                    onChange={(e) => setHeader(prev => ({ ...prev, in_time: e.target.value }))}
                  />
                </div>

                <div className="col-md-3">
                  <label style={styles.compactLabel}>Security Name (In)</label>
                  <input
                    className="form-control"
                    style={styles.compactInput}
                    value={header.security_name_in}
                    disabled={!isEditMode}
                    onChange={(e) => setHeader(prev => ({ ...prev, security_name_in: e.target.value }))}
                  />
                </div>

                <div className="col-md-3">
                  <label style={styles.compactLabel}>Out Time</label>
                  <input
                    type="time"
                    className="form-control"
                    style={styles.compactInput}
                    value={header.out_time}
                    disabled={!isEditMode}
                    onChange={(e) => setHeader(prev => ({ ...prev, out_time: e.target.value }))}
                  />
                </div>

                <div className="col-md-3">
                  <label style={styles.compactLabel}>Security Name (Out)</label>
                  <input
                    className="form-control"
                    style={styles.compactInput}
                    value={header.security_name_out}
                    disabled={!isEditMode}
                    onChange={(e) => setHeader(prev => ({ ...prev, security_name_out: e.target.value }))}
                  />
                </div>
              </div>

              {/* ================= TABS (Product / Docs only) ================= */}
              <div style={{ ...styles.tabs, marginBottom: "10px" }} className="gp-tabs">
                {["product", "docs"].map(tab => (
                  <button
                    key={tab}
                    className={`btn ${activeTab === tab ? "btn-secondary" : "btn-light"}`}
                    style={styles.compactTabBtn}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab === "docs" ? "Docs" : tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>

              {/* ================= PRODUCT TAB ================= */}
              {/* Rows load automatically when a P.O Number is selected above.
                  Without a PO, use "+ Add Product" to add rows manually —
                  Product Code / Description are then free-text on that row. */}
              {activeTab === "product" && (
                <div>
                  {isEditMode && (
                    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "8px" }}>
                      <button className="btn btn-dark btn-sm" onClick={addManualProduct}>
                        + Add Product
                      </button>
                    </div>
                  )}

                  {products.length === 0 ? (
                    <div style={{ ...styles.tableContainer, textAlign: 'center', color: '#64748b', fontSize: 13, padding: '24px 12px' }}>
                      Select a P.O Number above, or click "+ Add Product" to add items manually.
                    </div>
                  ) : (
                  <div style={{ ...styles.tableContainer, overflowX: "auto" }}>
                    <table className="table" style={{ ...styles.erpTable, tableLayout: "auto" }}>
                      <thead>
                        <tr>
                          <th style={{ ...styles.erpTh, width: 40, textAlign: 'center' }}>#</th>
                          <th style={{ ...styles.erpTh, width: 150 }}>Product Code</th>
                          <th style={{ ...styles.erpTh, minWidth: 160 }}>Description</th>
                          <th style={{ ...styles.erpTh, width: 110 }}>Package Type</th>
                          <th style={{ ...styles.erpTh, width: 90, textAlign: 'right' }}>No Of Package</th>
                          <th style={{ ...styles.erpTh, width: 90, textAlign: 'right' }}>Quantity</th>
                          <th style={{ ...styles.erpTh, width: 130, textAlign: 'right' }}>Qty as per Delivery Note</th>
                          <th style={{ ...styles.erpTh, width: 70, textAlign: 'center' }}>UOM</th>
                          <th style={{ ...styles.erpTh, width: 90, textAlign: 'right' }}>Weight</th>
                          <th style={{ ...styles.erpTh, minWidth: 150 }}>Reason for Movement</th>
                          {isEditMode && <th style={{ ...styles.erpTh, width: 40, textAlign: 'center' }}></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {products.map((row, idx) => (
                          <tr key={idx}>
                            <td style={{ ...styles.erpTd, textAlign: 'center' }}>{idx + 1}</td>
                            <td style={{ ...styles.erpTd, fontWeight: 600, minWidth: 170 }}>
                              {row.manual ? (
                                <Select
                                  styles={rsCompact}
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                  isDisabled={!isEditMode}
                                  isClearable
                                  placeholder="Select"
                                  options={productMaster.map(p => ({ value: p.productCode, label: `${p.productCode} - ${p.productDescription || ""}` }))}
                                  value={row.product_code ? { value: row.product_code, label: row.product_code } : null}
                                  onChange={(opt) => handleManualProductSelect(idx, opt?.value || "")}
                                />
                              ) : row.product_code}
                            </td>
                            <td style={{ ...styles.erpTd, minWidth: 160, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              {row.product_description}
                            </td>
                            <td style={{ ...styles.erpTd, minWidth: 150 }}>
                              <CreatableSelect
                                styles={rsPackageType}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                isDisabled={!isEditMode}
                                isClearable
                                backspaceRemovesValue
                                placeholder="Select..."
                                options={packageTypes.map(t => ({ value: t, label: t }))}
                                value={row.package_type ? { value: row.package_type, label: row.package_type } : null}
                                onChange={(opt) => handleProductFieldChange(idx, "package_type", opt?.value || "")}
                                formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                              />
                            </td>
                            <td style={styles.erpTd}>
                              <input
                                className="form-control form-control-sm p-1 text-end"
                                style={{ fontSize: '12px' }}
                                value={row.number}
                                disabled={!isEditMode}
                                onChange={(e) => handleProductFieldChange(idx, "number", e.target.value)}
                              />
                            </td>
                            <td style={{ ...styles.erpTd, textAlign: 'right' }}>
                              {row.manual ? (
                                <input
                                  className="form-control form-control-sm p-1 text-end"
                                  style={{ fontSize: '12px' }}
                                  value={row.quantity}
                                  disabled={!isEditMode}
                                  onChange={(e) => handleProductFieldChange(idx, "quantity", e.target.value)}
                                />
                              ) : row.quantity}
                            </td>
                            <td style={styles.erpTd}>
                              <input
                                type="number"
                                className="form-control form-control-sm p-1 text-end"
                                style={{ fontSize: '12px' }}
                                value={row.received_quantity ?? ""}
                                disabled={!isEditMode}
                                onChange={(e) => handleProductFieldChange(idx, "received_quantity", e.target.value)}
                              />
                            </td>
                            <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                              {row.manual ? (row.quantity_uom || <span style={{ color: '#94a3b8' }}>—</span>) : row.quantity_uom}
                            </td>
                            <td style={styles.erpTd}>
                              <input
                                className="form-control form-control-sm p-1 text-end"
                                style={{ fontSize: '12px' }}
                                value={row.weight}
                                disabled={!isEditMode}
                                onChange={(e) => handleProductFieldChange(idx, "weight", e.target.value)}
                              />
                            </td>
                            <td style={styles.erpTd}>
                              <input
                                className="form-control form-control-sm p-1"
                                style={{ fontSize: '12px' }}
                                value={row.reason_for_movement}
                                disabled={!isEditMode}
                                onChange={(e) => handleProductFieldChange(idx, "reason_for_movement", e.target.value)}
                              />
                            </td>
                           {isEditMode && (
  <td style={{ ...styles.erpTd, textAlign: 'center' }}>
    <button
      type="button"
      onClick={() => removeProduct(idx)}
      title="Delete row"
      style={{
        background: "transparent",
        border: "1px solid #dc3545",
        color: "#dc3545",
        width: "28px",
        height: "28px",
        borderRadius: "4px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        padding: 0
      }}
    >
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="3 6 5 6 21 6"></polyline>
        <path d="M19 6l-1 14H6L5 6"></path>
        <path d="M10 11v6"></path>
        <path d="M14 11v6"></path>
        <path d="M9 6V4h6v2"></path>
      </svg>
    </button>
  </td>
)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  )}
                </div>
              )}

              {/* ================= DOCS TAB ================= */}
              {activeTab === "docs" && (
                <div className="row g-3">

                  {/* ===== PDF VIEW / DOWNLOAD / PRINT CARD — same pattern as Goods Receipt ===== */}
                  {header.gate_pass_no && (
                    <div className="col-12">
                      <div style={{ border: "1px solid #dee2e6", borderRadius: "6px", padding: "16px", background: "#fff" }}>
                        <div style={{ marginBottom: "12px" }}>
                          <span style={{ fontSize: "18px", marginRight: "8px" }}>📄</span>
                          <span style={{ fontSize: "14px", fontWeight: 500, color: "#0f172a" }}>
                            {header.gate_pass_no}_GatePass.pdf
                          </span>
                        </div>
                        <div style={{ display: "flex", gap: "10px" }}>
                          <button className="btn btn-dark btn-sm" onClick={() => setShowGatePassViewModal(true)}>
                            View
                          </button>
                          <button
                            className="btn btn-dark btn-sm"
                            onClick={() => window.open(`/gatepass-view/${header.gate_pass_no}?action=download`, "_blank")}
                          >
                            Download
                          </button>
                          <button
                            className="btn btn-outline-dark btn-sm"
                            onClick={() => window.open(`/gatepass-view/${header.gate_pass_no}?action=print`, "_blank")}
                          >
                            Print
                          </button>
                        </div>
                      </div>

                      {showGatePassViewModal && (
                        <div
                          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}
                          onClick={(e) => { if (e.target === e.currentTarget) setShowGatePassViewModal(false); }}
                        >
                          <div style={{ width: "90%", height: "90%", background: "#fff", borderRadius: "8px", display: "flex", flexDirection: "column" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", borderBottom: "1px solid #e2e8f0" }}>
                              <span style={{ fontWeight: 600, fontSize: "14px" }}>{header.gate_pass_no}_GatePass.pdf</span>
                              <button onClick={() => setShowGatePassViewModal(false)} style={{ background: "none", border: "none", fontSize: "18px", cursor: "pointer", color: "#64748b" }}>
                                ✕
                              </button>
                            </div>
                            <iframe
                              src={`/gatepass-view/${header.gate_pass_no}`}
                              title="Gate Pass"
                              width="100%"
                              style={{ flex: 1, border: "none" }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Upload drop zone — same look as Goods Receipt's Docs tab */}
                  {(isEditMode || header.gate_pass_no) && (
                    <div className="col-12">
                      <label
                        htmlFor="gatePassDocFileInput"
                        style={{
                          display: "flex", flexDirection: "column", alignItems: "center",
                          justifyContent: "center", border: "2px dashed #cbd5e1", borderRadius: 8,
                          padding: "18px 12px", cursor: "pointer",
                          background: "#f8fafc"
                        }}
                        onDragOver={(e) => e.preventDefault()}
                      >
                        <span style={{ fontSize: 22 }}>📁</span>
                        <span style={{ fontSize: 13, color: "#475569", fontWeight: 500, marginTop: 4 }}>
                          {docsUploading ? "Uploading…" : "Click to attach documents"}
                        </span>
                        <span style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                          Photos or documents (max {maxGatePassAttachmentSizeKb} KB per file)
                        </span>
                        <input
                          id="gatePassDocFileInput"
                          type="file"
                          multiple
                          accept="image/*,.pdf,.doc,.docx"
                          style={{ display: "none" }}
                          onChange={handleAttachmentUpload}
                        />
                      </label>
                    </div>
                  )}

                  {/* Files list — same table layout as Goods Receipt's Docs tab */}
                  <div className="col-12">
                    {attachments.length === 0 ? (
                      <div style={{ textAlign: "center", padding: "28px 0", color: "#94a3b8", fontSize: 13 }}>
                        No attachments added
                        {!header.gate_pass_no && " — files chosen here upload once you Save the Gate Pass."}
                      </div>
                    ) : (
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                        <thead>
                          <tr>
                            <th style={styles.erpTh}>#</th>
                            <th style={styles.erpTh}>File Name</th>
                            <th style={styles.erpTh}>Size</th>
                            <th style={styles.erpTh}>Uploaded At</th>
                            <th style={styles.erpTh}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {attachments.map((file, idx) => {
                            const name = file.name || file.file_name || `Attachment ${idx + 1}`;
                            const sizeBytes = file.size ?? file.file_size;
                            const uploadedAt = file.uploadedAt || file.uploaded_at;
                            return (
                              <tr key={idx} style={{ background: idx % 2 === 0 ? "#fff" : "#f8fafc" }}>
                                <td style={styles.erpTd}>{idx + 1}</td>
                                <td style={styles.erpTd}>
                                  <span style={{ color: "#1e293b", fontWeight: 500 }}>{name}</span>
                                </td>
                                <td style={styles.erpTd}>
                                  {sizeBytes
                                    ? sizeBytes > 1024 * 1024
                                      ? `${(sizeBytes / (1024 * 1024)).toFixed(2)} MB`
                                      : `${(sizeBytes / 1024).toFixed(1)} KB`
                                    : '—'}
                                </td>
                                <td style={styles.erpTd}>{uploadedAt ? new Date(uploadedAt).toLocaleString() : '—'}</td>
                                <td style={styles.erpTd}>
                                  <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                                    {file.id ? (
                                      <button
                                        type="button"
                                        onClick={() => handleAttachmentDownload(file)}
                                        style={{
                                          display: 'inline-flex', alignItems: 'center', gap: 4,
                                          color: '#2563eb', fontWeight: 600, fontSize: 12,
                                          textDecoration: 'none', padding: '3px 10px',
                                          border: '1px solid #bfdbfe', borderRadius: 5,
                                          background: '#eff6ff', cursor: 'pointer'
                                        }}
                                      >
                                        ⬇ Download
                                      </button>
                                    ) : (
                                      // Not yet saved to Drive — locally staged only, so this
                                      // is the only stage where a person can un-stage a file.
                                      <button
                                        type="button"
                                        onClick={() => removeAttachment(idx)}
                                        style={{ background: "transparent", border: "none", color: "#ef4444", cursor: "pointer" }}
                                        title="Remove"
                                      >
                                        ✕
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}


            </div>

            {/* FOOTER */}
            <div style={{ ...styles.modalFooter, padding: "10px 14px" }}>
              {isEditMode ? (
                <>
                  <button className="btn btn-outline-secondary btn-sm" onClick={closeModal}>
                    Cancel
                  </button>
                  {/* New gate pass (no gate_pass_no yet) needs can_create; saving
                      changes to an existing one needs can_edit. */}
                  {(header.gate_pass_no ? gatePassPermission.can_edit : gatePassPermission.can_create) && (
                    <button
                      className="btn btn-dark btn-sm"
                      onClick={handleSave}
                      disabled={!isHeaderValid}
                      title={!isHeaderValid ? "Fill Date and Vehicle No to enable Save" : ""}
                    >
                      Save
                    </button>
                  )}
                </>
              ) : (
                gatePassPermission.can_edit && (
                  <button className="btn btn-dark btn-sm" onClick={() => setIsEditMode(true)}>
                    Edit
                  </button>
                )
              )}
            </div>

          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </Layout>
  );
};

const styles = {
  compactLabel: { fontSize: "12px", fontWeight: 600, marginBottom: "2px", color: "#334155" },
  compactInput: { fontSize: "14px", height: "32px", padding: "4px 8px" },
  compactTabBtn: { fontSize: "14px", padding: "4px 10px" },
  tableContainer: {
    background: "#ffffff", borderRadius: 8, padding: 12,
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)", overflowX: "auto", WebkitOverflowScrolling: "touch",
  },
  erpTable: { width: "100%", minWidth: "600px", tableLayout: "fixed", borderCollapse: "collapse" },
  erpTh: {
    background: "#f8fafc", fontSize: 13, fontWeight: 600, padding: "10px 12px",
    borderBottom: "1px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap",
  },
  erpTd: { fontSize: 13, padding: "10px 12px", borderBottom: "1px solid #f1f5f9", color: "#334155", verticalAlign: "middle" },
  filterCard: { background: '#ffffff', borderRadius: 8, padding: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 18 },
  filterLabel: { fontSize: 12, fontWeight: 600, marginBottom: 3, color: '#334155' },
  pageTitleBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: '10px' },
  pageTitle: { fontSize: 26, fontWeight: 700, color: '#1e293b', marginBottom: 2 },
  pageSubtitle: { fontSize: 13, color: '#64748b' },
  modalBackdrop: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1050, padding: '20px 12px', overflowY: 'auto',
  },
  modal: {
    background: '#fff', borderRadius: '8px', width: '95%', maxWidth: '1200px',
    maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
  },
  modalHeader: { padding: '15px 20px', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  iconBtn: { background: 'transparent', border: 'none', fontSize: '18px', cursor: 'pointer' },
  modalBody: { padding: '20px', overflowY: 'auto', flex: 1 },
  modalFooter: { padding: '15px 20px', borderTop: '1px solid #ddd', display: 'flex', justifyContent: 'flex-end', gap: '10px' },
  tabs: { display: 'flex', gap: 8, margin: '14px 0' },
};

export default GatePass;